import { useState } from 'react';

import BpkAriaLive from '@skyscanner-internal/backpack-web/bpk-component-aria-live';
import { useSelector } from 'react-redux';

import { FLIGHT_VERTICAL } from '../../helpers/constants/analyticsConstants';
import { GRID_SOURCE } from '../../helpers/constants/dataTypeConstants';
import { useDateSelection } from '../../helpers/hooks/useDateSelection';
import { useTranslation } from '../../helpers/hooks/useTranslation';
import { getSelectedDate } from '../../selectors';
import getMonthChartData from '../../selectors/barChartData';

import { Chart } from './Chart';
import { VerticalScale } from './VerticalScale';

import type { BarData } from './Chart';
import type { Direction, State } from '../../selectors/types';

import './ChartWithScale.scss';

type ChartWithScaleProps = {
  className?: string;
  direction: Direction;
  currencyFormatter: (value: number) => string;
  dateFormatter: (date: Date) => string;
  dayOfWeekNameProvider: (value: number | string) => string;
  hideScaleForWidthBelowPx: number;
  size: { width: number };
};

export const ChartWithScale = ({
  // className is consumed by SizeMe's wrapper div, not used directly here so descructured with underscore to show its not being used
  className: _className,
  currencyFormatter,
  dateFormatter,
  dayOfWeekNameProvider,
  direction,
  hideScaleForWidthBelowPx,
  size,
}: ChartWithScaleProps) => {
  const { selectDay } = useDateSelection();
  const translate = useTranslation();
  const [selectionAnnouncement, setSelectionAnnouncement] = useState('');

  const { barsData, scaleCoordinates } = useSelector((state: State) =>
    getMonthChartData(state, direction),
  );
  const selectedDate = useSelector((state: State) =>
    getSelectedDate(state, direction),
  );

  const hideVerticalScale = size.width < hideScaleForWidthBelowPx;

  const barsDataWithLabels: Array<BarData & { ariaLabel: string }> =
    barsData.map((bar) => {
      const dateLabel = bar.date
        ? dateFormatter(bar.date)
        : `${dayOfWeekNameProvider(bar.scaleValue ?? '')} ${bar.index}`;
      let label: string;

      if (bar.empty) {
        label = dateLabel;
      } else if (bar.price !== null && bar.price !== undefined) {
        const formattedPrice = currencyFormatter(bar.price);
        label = translate('date_price', {
          date: dateLabel,
          price: formattedPrice,
        });
      } else {
        label = translate('date_price_search_to_get_prices', {
          date: dateLabel,
        });
      }

      return { ...bar, ariaLabel: label };
    });

  const onBarClick = (index: number) => {
    const selectedBar = barsDataWithLabels.find((bar) => bar.index === index);

    selectDay(index, direction, GRID_SOURCE, FLIGHT_VERTICAL);

    if (selectedBar) {
      const directionLabel = translate(
        direction === 'outbound'
          ? 'outbound_month_label'
          : 'inbound_month_label',
      );
      setSelectionAnnouncement(`${directionLabel}: ${selectedBar.ariaLabel}`);
    }
  };

  return (
    <div
      className={`bar-chart-with-scales${
        hideVerticalScale ? ' no-vertical-scale' : ''
      }`}
    >
      <BpkAriaLive role="status" aria-atomic="true">
        {selectionAnnouncement}
      </BpkAriaLive>
      <div className="vertical-scale-area" aria-hidden="true">
        <VerticalScale
          coordinates={scaleCoordinates}
          valueFormatter={currencyFormatter}
        />
      </div>
      <div className="chart-area">
        <Chart
          barsData={barsDataWithLabels}
          onBarClick={onBarClick}
          maxColumnWidthPercent={100 / barsData.length}
          selectedIndex={selectedDate?.getDate() ?? null}
          chartIdentifier={`chart-${direction}`}
          scaleValueFormatter={dayOfWeekNameProvider}
        />
      </div>
    </div>
  );
};
